import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";
import Alert, { EAlertColor } from "../ui/Alert/Alert";
import Button, { EButtonColor } from "../ui/Button/Button";
import Spinner from "../ui/Spinner/Spinner";
import ModalBody from "../ui/Modal/ModalBody/ModalBody";
import ModalFooter from "../ui/Modal/ModalFooter/ModalFooter";

interface IProps {
  onCancel: () => void;
  defaultEmail?: string;
}

const PasswordResetModal: React.FC<IProps> = ({ onCancel, defaultEmail = "" }) => {
  const [email, setEmail] = useState<string>(defaultEmail);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSendResetEmail = async () => {
    if (!email || !email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSuccess(true);
    } catch (err: any) {
      console.error("Password reset error:", err);
      if (err.code === 'auth/user-not-found') {
        setError("User not found with this email address.");
      } else if (err.code === 'auth/invalid-email') {
        setError("Invalid email address format.");
      } else {
        setError(err.message || "Failed to send password reset email. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalBody style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {success ? (
          <Alert color={EAlertColor.SUCCESS}>
            A password reset link has been sent to <strong>{email}</strong>. Please check your inbox and follow the instructions to set a new password.
          </Alert>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: "0.95rem", color: "#444" }}>
              Enter your email address below. A link to reset your password will be sent to your email.
            </p>
            {error && <Alert color={EAlertColor.DANGER}>{error}</Alert>}
            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.25rem" }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "1rem",
                  boxSizing: "border-box"
                }}
              />
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {success ? (
          <Button onClick={onCancel} color={EButtonColor.PRIMARY}>
            Close
          </Button>
        ) : loading ? (
          <Spinner />
        ) : (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              disabled={!email || !email.includes("@")}
              onClick={handleSendResetEmail}
              color={EButtonColor.PRIMARY}
            >
              Send Reset Link
            </Button>
            <Button onClick={onCancel} color={EButtonColor.SECONDARY}>
              Cancel
            </Button>
          </div>
        )}
      </ModalFooter>
    </>
  );
};

export default PasswordResetModal;
